import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, makeObservable, observable } from 'mobx';

import { GetAnswerByGuidCollaboration } from '@/application/collaborations/exercises/get-answer-by-guid.collaboration';
import { GetExamAnswerByGuidCollaboration } from '@/application/collaborations/exercises/get-exam-answer-by-guid.collaboration';
import { GetExamExercisesCollaboration } from '@/application/collaborations/exercises/get-exam-exercises.collaboration';
import { GetExercisesByGuidCollaboration } from '@/application/collaborations/exercises/get-exercises-by-guid.collaboration';
import { PostExamAnswerByGuidCollaboration } from '@/application/collaborations/exercises/post-exam-answer-by-guid.collaboration';
import { PostExamStartCollaboration } from '@/application/collaborations/exercises/post-exam-start.collaboration';
import type { AnswerDto, ExamAnswerDto } from '@/application/interfaces/exercises/pegasus.gateway';
import { ClassifierDto } from '@/application/interfaces/pegasus.gateway';
import { Tracking } from '@/application/utils/tracking';
import { ApplicationModel } from '@/presentation/models/application.model';
import { StudentClassesStore } from '@/presentation/stores/studentClasses.store';

import { ExerciseType } from './types';

@ViewModel({ singleton: true })
export class StartExamViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable classifiers: ClassifierDto[] = [];
  @observable highestClassificationLevels: ClassifierDto[] = [];
  @observable answeredExercises = [];
  @observable exercises;
  @observable answered = false;
  @observable lessonGuid: string;
  @observable exercisesGuid: string;
  @observable currentStep = 0;
  @observable currentQuestion;
  @observable selectedOption = null;
  @observable showResult = false;
  @observable examExercises;
  @observable isQuiz = false;
  @observable showHighlight: boolean;
  @observable showHighlightCountdown = false;
  @observable countdown = 5;
  @observable buttonEnabled = false;

  constructor(
    private readonly applicationModel: ApplicationModel,
    private getExercisesByGuidCollaboration: GetExercisesByGuidCollaboration,
    private getExamAnswerByGuidCollaboration: GetExamAnswerByGuidCollaboration,
    private getAnswerByGuidCollaboration: GetAnswerByGuidCollaboration,
    private postExamStartCollaboration: PostExamStartCollaboration,
    private getExamExercisesCollaboration: GetExamExercisesCollaboration,
    private postExamAnswerByGuidCollaboration: PostExamAnswerByGuidCollaboration,
    private readonly studentClassesStore: StudentClassesStore,
  ) {
    makeObservable(this);
    this.showHighlight = false;
  }

  @action
  async onInit() {
    this.isLoading = false;
    Tracking.pageView(this.studentClassesStore.getStudentClassesTrackingInfo());
  }

  @action
  setClassifiers(classifiers: ClassifierDto[]) {
    this.classifiers = classifiers;
  }

  @action
  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  @action.bound
  setCurrentQuestion(question: any) {
    this.currentQuestion = question;
  }

  @action
  setShowResult(result: boolean) {
    this.showResult = result;
  }

  @action.bound
  setSelectedOption(selectedOption: string | null) {
    this.selectedOption = selectedOption;
  }

  @action
  setShowHighlight(value: boolean) {
    this.showHighlight = value;
  }

  @action.bound
  setShowHighlightCountdown(value: boolean) {
    this.showHighlightCountdown = value;
  }

  @action.bound
  setCountdown(value: number) {
    this.countdown = value;
  }

  @action.bound
  setButtonEnabled(value: boolean) {
    this.buttonEnabled = value;
  }

  get application() {
    return this.applicationModel.application;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async handleScreenEnter(guid: string): Promise<void> {
    this.isLoading = true;
    this.answeredExercises = [];
    try {
      this.exercisesGuid = guid;

      const { data: exercises } = await this.getExercisesByGuidCollaboration.run(guid);
      this.exercises = exercises;
      this.isQuiz = exercises.type === ExerciseType.QUIZ;

      this.showHighlight = !this.isQuiz;

      const { data: examExercises } = await this.getExamExercisesCollaboration.run(guid);
      this.examExercises = examExercises;

      if (this.examExercises.finished) {
        this.setShowResult(true);
      }

      if (this.exercises && this.exercises.exercisesQuestion.length > 0) {
        const { data: answerExercises } = await this.getExamAnswerByGuidCollaboration.run({
          guid,
        });

        if (answerExercises && answerExercises.answers.length > 0) {
          const questions = this.exercises.exercisesQuestion.map((exercise) => {
            answerExercises.answers.map((answer) => {
              if (answer.question_guid === exercise.question_guid) {
                exercise.answered = true;
                exercise.correct = answer.correct;
                exercise.choices_guid = answer.choices_guid;
                exercise.correct_choice = answer.correct_choice;

                this.answeredExercises.push({
                  choices_guid: exercise.choices_guid,
                  question_guid: exercise.question_guid,
                });
              }
            });

            return exercise;
          });

          this.exercises = { ...this.exercises, exercisesQuestion: questions };
        }
      }

      if (
        this.answeredExercises.length === this.exercises.exercisesQuestion.length ||
        this.examExercises.finished
      ) {
        this.setShowResult(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async getAnswerQuestion(dto: AnswerDto) {
    try {
      await this.getAnswerByGuidCollaboration.run(dto);
      await this.handleScreenEnter(this.exercisesGuid);
    } catch (error) {
      console.error(error);
    }
  }

  @action
  async setStartExam(guid: string) {
    try {
      const dto = {
        exercises_guid: guid,
        start_date: new Date(),
        finished: false,
      };
      await this.postExamStartCollaboration.run(dto);
      await this.handleScreenEnter(this.exercisesGuid);
    } catch (error) {
      console.error(error);
    }
  }

  @action
  async postExamAnswerQuestion(examAnswers: ExamAnswerDto) {
    try {
      await this.postExamAnswerByGuidCollaboration.run(examAnswers);
    } catch (error) {
      console.error(error);
    }
  }
}
